import type { MultiPropertySite, EnhancedMultiPropertySite } from '@/api/multi-property/types';
import type { Property } from '../mock-data';

/**
 * Transform multi-property API site data to frontend Property type
 */
export function transformApiSiteToProperty(apiSite: MultiPropertySite): Property {
  // Generate mock additional data for now (this would come from other API calls in production)
  // const mockMetrics = generateMockMetrics();
  // const mockStatus = generateMockStatus();
  
  return {
    id: apiSite.id,
    code: apiSite.site_code,
    name: apiSite.site_name_english,
    province: apiSite.province,
    region: apiSite.region_name,
    lat: apiSite.lat,
    lon: apiSite.lon,
    efficiency: null, // This would come from energy data API
    isOnline: apiSite.id === 'dohome-rama2' || apiSite.id === 'dohome-bang-phli' || apiSite.id === 'dohome-nakhon-sawan',
    hasAlarm: false,
    lastUpdated: null,
    metrics: null
  };
}

/**
 * Transform array of API sites to Property array
 */
export function transformApiSitesToProperties(apiSites: MultiPropertySite[]): Property[] {
  return apiSites.map(transformApiSiteToProperty);
}

/**
 * Format site name from site code and province
 */
function formatSiteName(siteCode: string, province: string): string {
  // Convert site code to title case and combine with province
  const formattedCode = siteCode.toLowerCase()
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  return `${formattedCode}`;
}

/**
 * Calculate mock EUI (Energy Use Intensity) based on area
 * In production, this would come from energy consumption API
 */
function calculateMockEUI(area: number): number {
  // Generate realistic EUI values based on area (smaller areas tend to have higher EUI)
  const baseEUI = area < 15000 ? 200 : area < 25000 ? 150 : 120;
  const variation = Math.random() * 100 - 50; // ±50 variation
  return Math.max(80, Math.round(baseEUI + variation));
}

/**
 * Generate mock metrics for sites
 * In production, these would come from separate API calls
 */
function generateMockMetrics() {
  return {
    powerConsumption: Math.floor(Math.random() * 2000) + 2000, // 2000-4000
    electricityConsumption: Math.floor(Math.random() * 1800) + 2200, // 2200-4000
    accumulatedRth: Math.floor(Math.random() * 2000) + 3000, // 3000-5000
  };
}

/**
 * Generate mock status information
 * In production, this would come from real-time status API
 */
function generateMockStatus() {
  const isOnline = Math.random() > 0.15; // 85% online rate
  const hasAlarm = isOnline && Math.random() > 0.7; // 30% alarm rate when online
  
  return {
    isOnline,
    hasAlarm,
    lastUpdated: new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    }).replace(/\//g, '-'),
  };
}

/**
 * Extract unique regions from API sites for filtering
 */
export function extractRegionsFromSites(apiSites: MultiPropertySite[]): string[] {
  const regions = new Set(apiSites.map(site => site.region_name));
  return ['All', ...Array.from(regions).sort()];
}

/**
 * Extract unique provinces from API sites for filtering
 */
export function extractProvincesFromSites(apiSites: MultiPropertySite[]): string[] {
  const provinces = new Set(apiSites.map(site => site.province));
  return ['All', ...Array.from(provinces).sort()];
}